import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Clock, Mail, MapPin, Phone, Info, Building } from 'lucide-react';
import getSettings from '../api/GetSettings';

const LibraryInfoModal = ({ isOpen, onClose }) => {
    const API_BASE = useSelector((state) => state.user.apiBase);
    const code_etablissement = useSelector((state) => state.user.currentUser.code_etablissement);
    const token = useSelector((state) => state.user.currentUser.token);
    const [info, setInfo] = useState({
        name: "",
        address: "",
        email: "",
        phone: "",
        logo: "",
        hours: []
    });
    const dispatch = useDispatch();
    useEffect(() => {
        if (API_BASE && token && code_etablissement) {
            const fetchSettings = async () => {
                try {
                    const settingsList = await getSettings(API_BASE, token, code_etablissement);
                    if (settingsList && Array.isArray(settingsList)) {
                        const templateSetting = settingsList.find(s => s.nom === 'late-return-email-template');
                        const delaySetting = settingsList.find(s => s.nom === 'delai-emprunt');

                        var linfo = { ...info };
                        linfo.address = settingsList.find(s => s.nom === 'library-address').valeur;
                        linfo.email = settingsList.find(s => s.nom === 'library-email').valeur;
                        linfo.phone = settingsList.find(s => s.nom === 'library-phone').valeur;
                        linfo.hours = settingsList.find(s => s.nom === 'library-hours').valeur;
                        const parsedHours = linfo.hours ? JSON.parse(linfo.hours) : [];
                        linfo.hours = parsedHours;
                        linfo.name = "Ma Bibliothèque " + code_etablissement;
                        linfo.logo = "/uploads/logos/logo_" + code_etablissement + ".png";
                        setInfo(linfo);

                    }
                } catch (error) {
                    console.error("Failed to fetch settings from backend:", error);
                }
            };
            fetchSettings();
        }
    }, [API_BASE, token, code_etablissement, isOpen]);

    if (!isOpen) return null;


    //const parsedHours = libraryHours ? JSON.parse(libraryHours) : [];



    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm lg:pl-72">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="bg-slate-900 border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[85vh]"
                >
                    {/* Header */}
                    <div className="p-6 border-b border-white/10 flex items-center justify-between bg-primary/10">
                        <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-primary/20 text-primary">
                                <Info size={24} />
                            </div>
                            <div>
                                <h3 className="text-xl font-bold text-white">Informations</h3>
                                <p className="text-sm text-slate-400">À propos de votre bibliothèque</p>
                            </div>
                        </div>
                        <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors p-2 hover:bg-white/5 rounded-full">
                            <X size={24} />
                        </button>
                    </div>

                    <div className="p-8 overflow-y-auto custom-scrollbar space-y-8">
                        {/* Logo and Name */}
                        <div className="flex flex-col items-center text-center space-y-4">
                            <div className="w-40 h-40 rounded-2xl bg-white/5 border border-white/10 p-4 flex items-center justify-center overflow-hidden">
                                {info.logo && !info.logo.includes("placeholder") ? (
                                    <img src={info.logo} alt="Logo" className="max-w-full max-h-full object-contain" />
                                ) : (
                                    <Building size={48} className="text-primary/40" />
                                )}
                            </div>
                            <div>
                                <h4 className="text-2xl font-bold text-white leading-tight">{info.name}</h4>
                            </div>
                        </div>

                        {/* Contact & Address Grid */}
                        <div className="grid grid-cols-1 gap-4">
                            <div className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/5">
                                <div className="p-2 rounded-lg bg-blue-500/10 text-blue-400">
                                    <MapPin size={20} />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-slate-300">Adresse</p>
                                    <p className="text-sm text-slate-400 leading-relaxed">{info.address}</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-4 p-4 rounded-xl bg-white/5 border border-white/5">
                                <div className="p-2 rounded-lg bg-emerald-500/10 text-emerald-400">
                                    <Mail size={20} />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-slate-300">Contact</p>
                                    <p className="text-sm text-slate-400">{info.email}</p>
                                    <p className="text-sm text-slate-400">{info.phone}</p>
                                </div>
                            </div>
                        </div>

                        {/* Opening Hours */}
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 text-amber-400">
                                <Clock size={20} />
                                <h5 className="font-bold">Horaires d'ouverture</h5>
                            </div>
                            <div className="bg-white/5 border border-white/5 rounded-xl overflow-hidden divide-y divide-white/5">
                                {info.hours.map((day, index) => (
                                    <div key={index} className="flex justify-between items-center p-3 px-4">
                                        <span className="text-sm text-slate-300 font-medium">{day.days}</span>
                                        <span className={`text-sm ${day.hours === 'Fermé' ? 'text-red-400/70' : 'text-slate-400'}`}>
                                            {day.hours}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="p-4 border-t border-white/10 bg-black/20 text-center">
                        <button
                            onClick={onClose}
                            className="px-6 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-sm font-medium transition-colors"
                        >
                            Fermer
                        </button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default LibraryInfoModal;
